import React, { useCallback, useEffect, useState } from 'react';
import { sendAuthenticatedRequest } from '../utils/api';
import Avatar from './Avatar';

// Who you have blocked, and the way to undo it.
//
// 2026-10-17: the phone has had this screen for a while (BlockedUsersScreen.js) and the web had no
// idea blocking existed. The gap was only ever cosmetic in one direction and serious in the other:
// a block made on the phone is enforced SERVER-side for both clients — isBlocked() in
// server/index.js:178 gates private messages (:628) and spot requests (:2230), from either side —
// so a web user was already protected by blocks they could not see. What they could not do is
// review the list or take anybody off it, which is the half that needs a screen.
//
// Rendered INSIDE the Profile panel rather than as a panel of its own, drilled into with Panel's
// own onBack — the same move Messages makes to open a conversation. A second panel sliding over the
// first would be a new navigation idea for one rarely-visited list.
const BlockedUsersList = () => {
  const [users, setUsers] = useState(null); // null = still loading, [] = genuinely empty
  const [failed, setFailed] = useState(false);
  const [unblocking, setUnblocking] = useState(null);

  const fetchBlocked = useCallback(async () => {
    try {
      // The same endpoint the phone reads. It returns blockedUserIds as well, which is what the
      // app's own block-state checks use; this list needs the fuller rows to render at all.
      const data = await sendAuthenticatedRequest('/blocks');
      setUsers(data.blockedUsers || []);
      setFailed(false);
    } catch (e) {
      setUsers([]);
      setFailed(true);
    }
  }, []);

  useEffect(() => { fetchBlocked(); }, [fetchBlocked]);

  const handleUnblock = async (user) => {
    setUnblocking(user.id);
    try {
      await sendAuthenticatedRequest(`/blocks/${user.id}`, 'DELETE');
      // Drop it locally rather than re-fetching: the row is gone either way, and a list that
      // flickers through a loading state to remove one line reads as a failure.
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
    } catch (e) {
      setFailed(true);
    } finally {
      setUnblocking(null);
    }
  };

  if (users === null) {
    return <p className="panel-empty">Loading…</p>;
  }

  if (failed && users.length === 0) {
    return (
      <p className="panel-empty">
        Could not load your blocked list.
        <br />
        Anyone you have blocked is still blocked — this is only the view that failed.
      </p>
    );
  }

  if (users.length === 0) {
    return (
      <p className="panel-empty">
        You haven’t blocked anyone.
        <br />
        Blocking someone stops messages and space requests between you, both ways.
      </p>
    );
  }

  return (
    <>
      {/* Said once, at the top: people arrive here to check what a block actually did. */}
      <p className="panel-note">
        Blocked people cannot message you or ask for your space, and you cannot reach them either.
      </p>
      {users.map((u) => (
        <div key={u.id} className="panel-row is-static">
          <Avatar username={u.username} avatarUrl={u.avatar_url} size={38} />
          <span className="panel-row-main">
            <span className="panel-row-title">{u.username}</span>
          </span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => handleUnblock(u)}
            disabled={unblocking === u.id}
          >
            {unblocking === u.id ? 'Unblocking…' : 'Unblock'}
          </button>
        </div>
      ))}
    </>
  );
};

export default BlockedUsersList;
